import { computed, Injectable, signal } from '@angular/core';
import { filter, fromEvent, map, Observable, pairwise, startWith } from 'rxjs';
import { Size } from '../models/size';

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  resize$!: Observable<[Size, Size]>;
  initialWindowHeight = window.innerHeight;
  heightWhenOpenedKeyboard = signal(0);
  counter = signal(0);
  keyboardHeight = computed(() => {
    return window.innerHeight - this.heightWhenOpenedKeyboard();
  });

  constructor() {
    this.resize$ = fromEvent(window, 'resize').pipe(
      startWith(null),
      map(() => ({ width: window.innerWidth, height: window.innerHeight })),
      pairwise(),
    );
  }

  onVerticalResize(): Observable<[number, number]> {
    return this.resize$.pipe(
      filter(([prev, curr]) => prev.height !== curr.height),
      map(([prev, curr]) => [prev.height, curr.height]),
    );
  }

  onHorizontalResize(): Observable<[number, number]> {
    return this.resize$.pipe(
      filter(([prev, curr]) => prev.width !== curr.width),
      map(([prev, curr]) => [prev.width, curr.width]),
    );
  }

  onChangeOrientation(): Observable<number> {
    return fromEvent(window, 'orientationchange').pipe(map(() => Math.random()));
  }

  isKeyboardOpen(): Observable<boolean> {
    return fromEvent(window.visualViewport!, 'resize').pipe(
      map(() => {
        if (window.visualViewport!.height + 100 < window.innerHeight) {
          this.heightWhenOpenedKeyboard.set(window.visualViewport!.height);
          return true;
        } else {
          return false;
        }
      }),
    );
  }

  shouldScrollToFocusedElement(): Observable<boolean> {
    return this.isKeyboardOpen().pipe(
      map(() => {
        const focusedElement = document.activeElement as HTMLElement;

        if (focusedElement) {
          if (focusedElement.getBoundingClientRect().top < window.visualViewport!.height) {
            return false;
          }
          if (['INPUT', 'TEXTAREA'].includes(focusedElement.tagName)) {
            window.scrollTo({ top: focusedElement.offsetTop, behavior: 'smooth' });
            return true;
          }
        }
        return false;
      }),
    );
  }
}
